import { formatDate, formatMoney, formatWeight, useTranslation } from "@roastery/i18n";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  PageHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from "@roastery/ui";
import { createFileRoute } from "@tanstack/react-router";
import { PlusIcon } from "lucide-react";
import { type ReactNode, useState } from "react";
import { type PortalVariant, usePortalCatalog, usePortalIdentity } from "../lib/portal-api";
import { cartKeyOf, useCart } from "../lib/use-cart";
import { useIsWide } from "../lib/use-media-query";
import { usePortalFormatSettings } from "../lib/use-portal-format";

export const Route = createFileRoute("/_authed/")({
  component: CatalogPage,
});

/**
 * The shelf (US-10.2). Every price here is this customer's own — the roastery's retail price is
 * nowhere on this screen, because it is not what they pay and showing both would invite a
 * conversation about the difference.
 *
 * <p>Ordering (US-10.3) happens from this table rather than from a product page: a wholesale buyer
 * knows what they want and is refilling, not browsing. One box for the count, one button, and the
 * cart bar at the bottom keeps the running total in sight.
 */
function CatalogPage() {
  const { t } = useTranslation();
  const catalog = usePortalCatalog();
  const identity = usePortalIdentity();
  const cart = useCart(cartKeyOf(identity.data?.portalUserId, identity.data?.customerId));
  // A café buying for its decaf tap should not have to read every card to find one (US-28.6).
  const [decafOnly, setDecafOnly] = useState(false);
  const all = catalog.data?.products ?? [];
  const products = decafOnly ? all.filter((product) => product.caffeine === "DECAF") : all;

  if (catalog.isLoading) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="portal-catalog-loading">
        {t("app.loading")}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="portal-catalog">
      <PageHeader
        title={t("portal.catalog.title")}
        description={t("portal.catalog.description")}
        actions={
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="size-4 rounded border-input accent-primary"
              data-testid="portal-filter-decaf"
              checked={decafOnly}
              onChange={(event) => setDecafOnly(event.target.checked)}
            />
            {t("portal.catalog.decafOnly")}
          </label>
        }
      />

      {products.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="portal-catalog-empty">
          {t("portal.catalog.empty")}
        </p>
      ) : (
        products.map((product) => (
          <Card key={product.id} data-testid={`portal-product-${product.code}`}>
            <CardHeader>
              {/* The photograph and the words, both in the language this portal is being read in
                  (US-23.4). The roastery uploads one picture; whether it is ours or an address on
                  their own site is settled server-side, so nothing here has to ask. */}
              <div className="flex items-start gap-4">
                {product.imageUrl ? (
                  <img
                    src={product.imageUrl}
                    alt=""
                    className="size-20 shrink-0 rounded border bg-white object-cover"
                    data-testid={`portal-product-image-${product.code}`}
                  />
                ) : null}
                <div className="flex min-w-0 flex-col gap-1.5">
                  <CardTitle className="flex flex-wrap items-center gap-2">
                    {product.name}
                    {(product.tags ?? []).map((tag) => (
                      <Badge key={tag} variant="muted">
                        {tag}
                      </Badge>
                    ))}
                  </CardTitle>
                  {product.description ? <CardDescription>{product.description}</CardDescription> : null}
                  {product.tastingNotes ? (
                    <p className="text-sm text-muted-foreground" data-testid={`portal-notes-${product.code}`}>
                      {product.tastingNotes}
                    </p>
                  ) : null}
                  {/* What food law asks the bag to say (US-28.6). A buyer ordering for a café has
                      to be able to read it here rather than telephone about it. */}
                  <span className="flex flex-wrap items-center gap-2">
                    {product.caffeine === "DECAF" ? (
                      <Badge variant="secondary" data-testid={`portal-decaf-${product.code}`}>
                        {t("portal.catalog.decaf")}
                      </Badge>
                    ) : null}
                    {product.caffeine === "HALF_CAF" ? (
                      <Badge variant="secondary">{t("portal.catalog.halfCaf")}</Badge>
                    ) : null}
                    {product.flavoured ? (
                      <Badge variant="secondary" data-testid={`portal-flavoured-${product.code}`}>
                        {t("portal.catalog.flavoured")}
                      </Badge>
                    ) : null}
                  </span>
                  {product.allergenTraces ? (
                    <p
                      className="text-xs text-muted-foreground"
                      data-testid={`portal-allergens-${product.code}`}
                    >
                      {product.allergenTraces}
                    </p>
                  ) : null}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <VariantList
                productCode={product.code ?? ""}
                variants={product.variants ?? []}
                renderAdd={(variant, stacked) => (
                  <AddToCart
                    variant={variant}
                    productName={product.name ?? ""}
                    inCart={cart.quantityOf(variant.id ?? "")}
                    stacked={stacked}
                    onAdd={(quantity) => {
                      cart.add(
                        {
                          variantId: variant.id ?? "",
                          sku: variant.sku ?? "",
                          productName: product.name ?? "",
                          packageSizeGrams: variant.packageSizeGrams ?? 0,
                          grind: variant.grind,
                          unitPriceNet: Number(variant.priceNet ?? 0),
                        },
                        quantity,
                      );
                      toast.success(t("portal.cart.added", { sku: variant.sku, count: quantity }));
                    }}
                  />
                )}
              />
            </CardContent>
          </Card>
        ))
      )}

      <p className="text-xs text-muted-foreground" data-testid="portal-catalog-note">
        {t("portal.catalog.note")}
      </p>
    </div>
  );
}

/**
 * A product's sizes and grinds: a table from `sm` up, a card per variant on a phone, where five
 * columns pushed the price and the Add button off the right edge of the screen.
 */
function VariantList({
  productCode,
  variants,
  renderAdd,
}: {
  productCode: string;
  variants: PortalVariant[];
  renderAdd: (variant: PortalVariant, stacked: boolean) => ReactNode;
}) {
  const { t } = useTranslation();
  const settings = usePortalFormatSettings();
  const wide = useIsWide();

  if (!wide) {
    return (
      <ul className="flex flex-col gap-3" data-testid={`portal-variants-${productCode}`}>
        {variants.map((variant) => (
          <li
            key={variant.id}
            className="flex flex-col gap-2 rounded-md border border-border p-3"
            data-testid={`portal-variant-${variant.sku}`}
          >
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
              <span className="font-medium">
                {formatWeight(variant.packageSizeGrams ?? 0, settings)}
                {variant.grind ? ` · ${t(`portal.grind.${variant.grind}`)}` : null}
              </span>
              <span className="tabular-nums font-semibold">
                {formatMoney(Number(variant.priceNet ?? 0), settings)}
              </span>
            </div>
            <div>
              <AvailabilityBadge variant={variant} />
            </div>
            {renderAdd(variant, true)}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <Table data-testid={`portal-variants-${productCode}`}>
      <TableHeader>
        <TableRow>
          <TableHead>{t("portal.catalog.columns.size")}</TableHead>
          <TableHead>{t("portal.catalog.columns.grind")}</TableHead>
          <TableHead>{t("portal.catalog.columns.price")}</TableHead>
          <TableHead>{t("portal.catalog.columns.availability")}</TableHead>
          <TableHead className="text-right">{t("portal.catalog.columns.order")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {variants.map((variant) => (
          <TableRow key={variant.id} data-testid={`portal-variant-${variant.sku}`}>
            <TableCell className="font-medium">{formatWeight(variant.packageSizeGrams ?? 0, settings)}</TableCell>
            <TableCell className="text-muted-foreground">
              {variant.grind ? t(`portal.grind.${variant.grind}`) : "—"}
            </TableCell>
            <TableCell className="tabular-nums font-medium">
              {formatMoney(Number(variant.priceNet ?? 0), settings)}
            </TableCell>
            <TableCell>
              <AvailabilityBadge variant={variant} />
            </TableCell>
            <TableCell className="text-right">{renderAdd(variant, false)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/**
 * Three answers since US-31.3. A shelf that is entirely still degassing used to read "to order",
 * which was a promise nobody had to keep; "available from Thursday" is one the roastery can.
 * Ordering is allowed either way.
 */
function AvailabilityBadge({ variant }: { variant: PortalVariant }) {
  const { t } = useTranslation();
  const settings = usePortalFormatSettings();
  return (
    <Badge variant={variant.availability === "IN_STOCK" ? "outline" : "muted"}>
      {variant.availability === "RESTING" && variant.availableFrom
        ? t("portal.availability.RESTING_FROM", { date: formatDate(variant.availableFrom, settings) })
        : t(`portal.availability.${variant.availability}`)}
    </Badge>
  );
}

/** How many packs, and into the cart. The count resets after each add, so refilling is repeatable. */
function AddToCart({
  variant,
  productName,
  inCart,
  stacked,
  onAdd,
}: {
  variant: PortalVariant;
  productName: string;
  inCart: number;
  /** On a phone: the count and a full-width button under the variant, not squeezed beside it. */
  stacked: boolean;
  onAdd: (quantity: number) => void;
}) {
  const { t } = useTranslation();
  const [quantity, setQuantity] = useState(1);

  return (
    <div className={stacked ? "flex flex-wrap items-center gap-2" : "flex items-center justify-end gap-2"}>
      {inCart > 0 ? (
        <span
          className={stacked ? "w-full text-xs text-muted-foreground" : "text-xs text-muted-foreground"}
          data-testid={`portal-in-cart-${variant.sku}`}
        >
          {t("portal.cart.inCart", { count: inCart })}
        </span>
      ) : null}
      <Input
        type="number"
        min={1}
        max={9999}
        value={quantity}
        aria-label={t("portal.cart.quantityFor", { product: productName, sku: variant.sku })}
        data-testid={`portal-quantity-${variant.sku}`}
        className="w-20 shrink-0 tabular-nums"
        onChange={(event) => setQuantity(Math.max(1, Math.min(9999, Number(event.target.value) || 1)))}
      />
      <Button
        size={stacked ? "default" : "sm"}
        variant="outline"
        className={stacked ? "flex-1" : undefined}
        data-testid={`portal-add-${variant.sku}`}
        onClick={() => {
          onAdd(quantity);
          setQuantity(1);
        }}
      >
        <PlusIcon aria-hidden />
        {t("portal.cart.add")}
      </Button>
    </div>
  );
}
